import { useEffect, useState, useCallback, useMemo, useContext } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Card, Table, Tag, Typography, Space, Segmented, Button, Drawer, Empty, Spin } from "antd";
import { ReloadOutlined, EyeOutlined } from "@ant-design/icons";
import FinalSettlementPanel from "./FinalSettlementPanel";
import { AuthContext } from "../../context/AuthContext";


const { Title, Text } = Typography;

const STATUS_TAG = {
    PENDING: { color: "gold", label: "Pending" },
    APPROVED: { color: "success", label: "Approved" },
    REJECTED: { color: "error", label: "Rejected" },
};

const money = (v) =>
    `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 0 })}`;

const fmtDateTime = (v) => (v ? dayjs(v).format("DD MMM YYYY, hh:mm A") : "—");

/**
 * Superuser's Full & Final Settlement approval queue.
 *
 * Lists every settlement that has been generated (one row per employee),
 * filterable by status. Clicking a row opens the same FinalSettlementPanel
 * used elsewhere in the app (preview + Excel download work identically),
 * with Approve / Reject controls enabled.
 */
export default function FinalSettlementApprovalsQueue({ authAccess, messageApi }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("PENDING");
    const [selectedExitProcessId, setSelectedExitProcessId] = useState(null);

    const { authData, backUrl } = useContext(AuthContext);

    const notify =
        messageApi || {
            success: (m) => console.log("[success]", m),
            error: (m) => console.error("[error]", m),
        };

    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${authData?.access}` }),
        [authData?.access]
    );

    const load = useCallback(async () => {
        // if (!authData?.access || !backUrl) {
        //     setLoading(false);
        //     return;
        // }
        setLoading(true);
        try {
            const params = statusFilter === "ALL" ? {} : { status: statusFilter };
            const res = await axios.get(`${backUrl}/api/exitprocess/final-settlement-approvals/`, {
                headers: authHeaders,
                params,
            });
            // DRF may return a plain array or a paginated { results: [...] } object
            // depending on your REST_FRAMEWORK settings — normalize either way.
            const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
            setRows(data);
        } catch (err) {
            notify.error(err.response?.data?.detail || "Could not load the approval queue.");
            setRows([]); // never leave rows as something non-array on error either
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [backUrl, statusFilter]);

    useEffect(() => {
        load();
    }, [load]);

    const columns = [
        {
            title: "Employee",
            key: "employee",
            render: (_, row) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{row.employee_name || "—"}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {row.emp_code} {row.designation ? `· ${row.designation}` : ""}
                    </Text>
                </Space>
            ),
        },
        {
            title: "Department",
            dataIndex: "department",
            key: "department",
            render: (v) => v || "—",
        },
        {
            title: "Net Payment",
            dataIndex: "net_payment",
            key: "net_payment",
            align: "right",
            render: (v) => money(v),
        },
        {
            title: "Submitted",
            key: "submitted",
            render: (_, row) => (
                <Space direction="vertical" size={0}>
                    <Text style={{ fontSize: 12 }}>{fmtDateTime(row.submitted_at)}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        by {row.submitted_by ? `${row.submitted_by.first_name || ""} ${row.submitted_by.last_name || ""}`.trim() : "—"}
                    </Text>
                </Space>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (v) => {
                const tag = STATUS_TAG[v] || { color: "default", label: v };
                return <Tag color={tag.color}>{tag.label}</Tag>;
            },
        },
        {
            title: "",
            key: "actions",
            render: (_, row) => (
                <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => setSelectedExitProcessId(row.exit_process_id)}
                >
                    View
                </Button>
            ),
        },
    ];

    return (
        <div className="ffs-queue-wrapper">
            <Card
                className="ffs-card"
                bordered
                title={
                    <Space direction="vertical" size={0}>
                        <Title level={4} style={{ margin: 0 }}>
                            Full & Final Settlement Approvals
                        </Title>
                        {/* <Text type="secondary" style={{ fontSize: 13 }}>
                            Every generated settlement shows up here for sign-off.
                        </Text> */}
                    </Space>
                }
                extra={
                    <Space>
                        <Segmented
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={[
                                { label: "Pending", value: "PENDING" },
                                { label: "Approved", value: "APPROVED" },
                                { label: "Rejected", value: "REJECTED" },
                                { label: "All", value: "ALL" },
                            ]}
                        />
                        <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                    </Space>
                }
            >
                {loading ? (
                    <div style={{ textAlign: "center", padding: 40 }}>
                        <Spin tip="Loading…" />
                    </div>
                ) : rows.length === 0 ? (
                    <Empty description="No settlements in this view." />
                ) : (
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={rows}
                        pagination={{ pageSize: 10 }}
                        onRow={(row) => ({
                            style: { cursor: "pointer" },
                            onClick: () => setSelectedExitProcessId(row.exit_process_id),
                        })}
                        components={{
                            header: {
                                cell: (props) => (
                                    <th {...props} style={{ backgroundColor: "#0e497b", color: 'white', marginTop: '20px' }}>
                                        {props.children}
                                    </th>
                                ),
                            },
                        }}
                        bordered
                    />
                )}
            </Card>

            <Drawer
                open={!!selectedExitProcessId}
                onClose={() => setSelectedExitProcessId(null)}
                width="min(900px, 100vw)"
                title="Full & Final Settlement"
                destroyOnClose
            >
                {selectedExitProcessId && (
                    <FinalSettlementPanel
                        exitProcessId={selectedExitProcessId}
                        backUrl={backUrl}
                        authAccess={authAccess}
                        messageApi={messageApi}
                        viewerIsSuperuser
                        onApprovalDecided={() => {
                            load();
                        }}
                    />
                )}
            </Drawer>
        </div>
    );
}